import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Avatar,
  IconButton,
  Grid,
  Chip,
  Alert,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Helmet } from 'react-helmet-async';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import VideocamIcon from '@mui/icons-material/Videocam';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import TuneIcon from '@mui/icons-material/Tune';
import PaletteIcon from '@mui/icons-material/Palette';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import StorageIcon from '@mui/icons-material/Storage';
import PersonIcon from '@mui/icons-material/Person';
import NotesIcon from '@mui/icons-material/Notes';
import toast from 'react-hot-toast';
import { getUserDetails, validatePassword } from '../utils';
import { useThemeMode } from '../contexts/ThemeContext';
import { useMutationChangePassword } from '../hooks/react-query/useData';
import {
  useMutationEnable2FA,
  useMutationDisable2FA,
  useQueryCheck2FAStatus,
} from '../hooks/react-query/twoFactor';

// TabPanel: defined outside Settings so it isn't recreated on each render (prevents input focus loss)
function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
    >
      <Box sx={{ pt: 3 }}>{children}</Box>
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

export default function Settings() {
  const navigate = useNavigate();
  const userDetails = getUserDetails();
  const userRole = userDetails?.role?.toLowerCase();
  const isEdifier = userRole === 'coach';
  const isTeacher = userRole === 'teacher';
  const { themeMode, changeThemeMode } = useThemeMode();

  const handleClosePassword = () => {
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordErrors({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPasswordFields(false);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };
  const { mutate: changePassword, isLoading: isPasswordUpdating } = useMutationChangePassword(
    toast,
    userDetails?.token,
    navigate,
    handleClosePassword
  );

  // Profile state
  const [profileData, setProfileData] = useState({
    firstName: userDetails?.fname || '',
    lastName: userDetails?.lname || '',
    email: userDetails?.email || '',
    phone: userDetails?.phone || '',
    location: `${userDetails?.info?.city || ''}, ${userDetails?.info?.state || ''}`,
    bio: userDetails?.info?.interestBio || '',
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileImage, setProfileImage] = useState(userDetails?.image || '/assets/ProfileAvatar.png');

  // Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [emailVerified, setEmailVerified] = useState(true); // Mock state
  
  // Check 2FA status on mount
  const { data: twoFactorStatus } = useQueryCheck2FAStatus(userDetails?.token);
  
  // Update local state when 2FA status is fetched
  React.useEffect(() => {
    if (twoFactorStatus?.data?.data) {
      setTwoFactorEnabled(twoFactorStatus.data.data.is2FAEnabled || false);
    }
  }, [twoFactorStatus]);

  // 2FA mutations
  const handleEnable2FASuccess = (data) => {
    setTwoFactorEnabled(true);
    // Update user data in localStorage
    const currentUserData = JSON.parse(localStorage.getItem('user')) || {};
    localStorage.setItem('user', JSON.stringify({ ...currentUserData, is2FAEnabled: true }));
  };

  const handleDisable2FASuccess = (data) => {
    setTwoFactorEnabled(false);
    // Update user data in localStorage
    const currentUserData = JSON.parse(localStorage.getItem('user')) || {};
    localStorage.setItem('user', JSON.stringify({ ...currentUserData, is2FAEnabled: false }));
  };

  const { mutate: enable2FA, isLoading: isEnabling2FA } = useMutationEnable2FA(
    toast,
    userDetails?.token,
    handleEnable2FASuccess
  );

  const { mutate: disable2FA, isLoading: isDisabling2FA } = useMutationDisable2FA(
    toast,
    userDetails?.token,
    handleDisable2FASuccess
  );

  // Notification preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailNotifications: true,
    pushNotifications: true,
    meetingReminders: true,
    goalUpdates: true,
    messageNotifications: true,
    weeklyDigest: false,
  });

  // Video calling preferences
  const [videoCallPrefs, setVideoCallPrefs] = useState({
    autoJoinVideo: true,
    enableMicrophone: true,
    enableCamera: true,
    preferredResolution: '720p',
    enableScreenShare: true,
  });

  // Availability calendar (for Edifier/Teacher)
  const [availabilitySettings, setAvailabilitySettings] = useState({
    timezone: 'America/New_York',
    defaultAvailability: 'business-hours',
    allowWeekendBookings: false,
    advanceBookingDays: 30,
  });

  // Preferences state
  const [preferences, setPreferences] = useState({
    // Display & Interface
    language: 'en',
    compactView: false,
    displayDensity: 'comfortable',
    
    // Privacy
    profileVisibility: 'all',
    showEmailToOthers: false,
    showPhoneToOthers: false,
    allowPairingRequests: true,
    
    // Communication
    preferredContactMethod: 'email',
    autoAcceptMeetings: false,
    
    // Session Preferences
    defaultSessionDuration: 60,
    sessionReminderTime: 15,
    enableSessionRecording: false,
    
    // Calendar
    calendarView: 'week',
    showDeclinedEvents: false,
    syncWithGoogleCalendar: false,
    
    // Data & Storage
    autoSaveNotes: true,
    keepSessionHistory: true,
    dataRetentionPeriod: 'forever',
  });

  // Login activity (mock data)
  const [loginActivity] = useState([
    {
      id: 1,
      device: 'Chrome on Windows',
      location: 'New York, USA',
      ip: '192.168.1.1',
      time: '2 hours ago',
      current: true,
    },
    {
      id: 2,
      device: 'Safari on iPhone',
      location: 'New York, USA',
      ip: '192.168.1.2',
      time: '1 day ago',
      current: false,
    },
    {
      id: 3,
      device: 'Chrome on Mac',
      location: 'Boston, USA',
      ip: '192.168.1.3',
      time: '3 days ago',
      current: false,
    },
  ]);

  const handleProfileImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileUpdate = () => {
    // Mock save functionality
    setIsEditingProfile(false);
    // In real implementation, call API here
  };

  const getPasswordFormErrors = () => {
    const errors = { currentPassword: '', newPassword: '', confirmPassword: '' };
    const current = passwordData.currentPassword?.trim() || '';
    const newPwd = passwordData.newPassword;
    const confirm = passwordData.confirmPassword;

    if (!current) errors.currentPassword = 'Current password is required';
    const newPwdValidation = validatePassword(newPwd);
    if (newPwdValidation !== true) errors.newPassword = newPwdValidation;
    if (newPwd && confirm && newPwd !== confirm) errors.confirmPassword = 'Passwords do not match';
    else if (!confirm.trim()) errors.confirmPassword = 'Please confirm your new password';
    if (current && newPwd && current === newPwd && !errors.newPassword) errors.newPassword = 'New password must be different from current password';

    return errors;
  };

  const handlePasswordChange = () => {
    const errors = getPasswordFormErrors();
    setPasswordErrors(errors);
    const hasError = errors.currentPassword || errors.newPassword || errors.confirmPassword;
    if (hasError) return;
    changePassword({
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
    });
  };

  const handleNotificationToggle = (key) => {
    setNotificationPrefs((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleVideoCallToggle = (key) => {
    setVideoCallPrefs((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleTwoFactorToggle = () => {
    if (!userDetails?.phone) {
      toast.error('Please add a phone number to your profile before enabling 2FA');
      return;
    }

    if (twoFactorEnabled) {
      // Disable 2FA
      disable2FA();
    } else {
      // Enable 2FA
      enable2FA();
    }
  };

  const handleEmailVerification = () => {
    setEmailVerified(true);
    // In real implementation, send verification email
  };

  const handlePreferenceChange = (key, value) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePreferenceToggle = (key) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSavePreferences = () => {
    // In real implementation, call API to save preferences
    console.log('Saving preferences:', preferences);
  };

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <>
      <Helmet>
        <title>Settings | Edifying Teachers</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {/* Page Header */}
          <Typography
            variant="h3"
            sx={{
              color: 'text.titles',
              mb: 3,
              ml: { xs: '20px', sm: '0' },
            }}
          >
            Settings
          </Typography>

          <Grid container spacing={3}>
          {/* Left Column - Main Settings */}
          <Grid item xs={12} lg={8}>
            {/* Tabs Container */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 3,
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
              }}
            >
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={currentTab}
                  onChange={handleTabChange}
                  aria-label="settings tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      minHeight: 72,
                      color: '#666',
                      '&.Mui-selected': {
                        color: 'main.main',
                      },
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: 'main.main',
                      height: 3,
                    },
                  }}
                >
                  <Tab label="Profile" icon={<EditIcon />} iconPosition="start" {...a11yProps(0)} />
                  <Tab label="Security" icon={<SecurityIcon />} iconPosition="start" {...a11yProps(1)} />
                  <Tab label="Preferences" icon={<TuneIcon />} iconPosition="start" {...a11yProps(2)} />
                </Tabs>
              </Box>

              <Box sx={{ p: { xs: 3, sm: 4 } }}>
                {/* Profile Tab */}
                <TabPanel value={currentTab} index={0}>
                  {/* Profile Update Section */}
                <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'main.main',
                      width: 40,
                      height: 40,
                      mr: 2,
                    }}
                  >
                    <EditIcon />
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'main.main' }}>
                    Profile Information
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Profile Photo (display only) */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 3 }}>
                  <Avatar
                    src={profileImage}
                    alt="Profile"
                    sx={{
                      width: 120,
                      height: 120,
                      border: '4px solid',
                      borderColor: 'main.main',
                      boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {!profileImage && userDetails?.fname?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                      Profile Photo
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Your profile picture
                    </Typography>
                  </Box>
                </Box>

                {/* Profile information with icons (read-only) - fixed icon width for aligned content */}
                <Stack spacing={2.5}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ width: 30, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                      <PersonIcon sx={{ color: 'text.secondary', mt: 0.5, fontSize: 22 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Name</Typography>
                      <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                        {profileData.firstName || profileData.lastName
                          ? [profileData.firstName, profileData.lastName].filter(Boolean).join(' ')
                          : '—'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ width: 30, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                      <EmailIcon sx={{ color: 'text.secondary', mt: 0.5, fontSize: 22 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Email</Typography>
                      <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                        {profileData.email || '—'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: '1 1 200px', minWidth: 0 }}>
                      <Box sx={{ width: 30, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                        <PhoneIcon sx={{ color: 'text.secondary', mt: 0.5, fontSize: 22 }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Phone</Typography>
                        <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                          {profileData.phone || '—'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: '1 1 200px', minWidth: 0 }}>
                      <Box sx={{ width: 30, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                        <LocationOnIcon sx={{ color: 'text.secondary', mt: 0.5, fontSize: 22 }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Location</Typography>
                        <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                          {profileData.location?.trim() || '—'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ width: 30, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                      <NotesIcon sx={{ color: 'text.secondary', mt: 0.5, fontSize: 22 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Bio</Typography>
                      <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500, whiteSpace: 'pre-wrap' }}>
                        {profileData.bio?.trim() || '—'}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
                  </TabPanel>

                  {/* Security Tab */}
                  <TabPanel value={currentTab} index={1}>
                    {/* Change Password Section */}
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Avatar
                            sx={{
                              bgcolor: 'primary.main',
                              width: 40,
                              height: 40,
                              mr: 2,
                            }}
                          >
                            <LockIcon />
                          </Avatar>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            Change Password
                          </Typography>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        {!showPasswordFields ? (
                          <Box>
                            <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
                              Update your password to keep your account secure.
                            </Typography>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => setShowPasswordFields(true)}
                              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                            >
                              Change Password
                            </Button>
                          </Box>
                        ) : (
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                type={showCurrentPassword ? 'text' : 'password'}
                                label="Current Password"
                                placeholder="Enter your current password"
                                value={passwordData.currentPassword}
                                error={!!passwordErrors.currentPassword}
                                helperText={passwordErrors.currentPassword}
                                onChange={(e) => {
                                  setPasswordData({ ...passwordData, currentPassword: e.target.value });
                                  if (passwordErrors.currentPassword) setPasswordErrors((prev) => ({ ...prev, currentPassword: '' }));
                                }}
                                onBlur={() => {
                                  const current = passwordData.currentPassword?.trim();
                                  if (!current) setPasswordErrors((prev) => ({ ...prev, currentPassword: 'Current password is required' }));
                                }}
                                sx={{ mb: 2 }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                                        onClick={() => setShowCurrentPassword((prev) => !prev)}
                                        edge="end"
                                        size="small"
                                      >
                                        {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                type={showNewPassword ? 'text' : 'password'}
                                label="New Password"
                                placeholder="8–30 characters, with upper, lower, number and special character"
                                value={passwordData.newPassword}
                                error={!!passwordErrors.newPassword}
                                helperText={passwordErrors.newPassword}
                                onChange={(e) => {
                                  setPasswordData({ ...passwordData, newPassword: e.target.value });
                                  if (passwordErrors.newPassword) setPasswordErrors((prev) => ({ ...prev, newPassword: '' }));
                                  if (passwordErrors.confirmPassword && passwordData.confirmPassword === e.target.value) setPasswordErrors((prev) => ({ ...prev, confirmPassword: '' }));
                                }}
                                onBlur={() => {
                                  const newPwd = passwordData.newPassword;
                                  const validation = validatePassword(newPwd);
                                  if (validation !== true) setPasswordErrors((prev) => ({ ...prev, newPassword: validation }));
                                  else if (passwordData.currentPassword?.trim() && newPwd && passwordData.currentPassword === newPwd) setPasswordErrors((prev) => ({ ...prev, newPassword: 'New password must be different from current password' }));
                                  else setPasswordErrors((prev) => ({ ...prev, newPassword: '' }));
                                }}
                                sx={{ mb: 2 }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                                        onClick={() => setShowNewPassword((prev) => !prev)}
                                        edge="end"
                                        size="small"
                                      >
                                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                type={showConfirmPassword ? 'text' : 'password'}
                                label="Confirm New Password"
                                placeholder="Re-enter your new password"
                                value={passwordData.confirmPassword}
                                error={!!passwordErrors.confirmPassword}
                                helperText={passwordErrors.confirmPassword}
                                onChange={(e) => {
                                  setPasswordData({ ...passwordData, confirmPassword: e.target.value });
                                  if (passwordErrors.confirmPassword) setPasswordErrors((prev) => ({ ...prev, confirmPassword: '' }));
                                  if (passwordData.newPassword && e.target.value === passwordData.newPassword) setPasswordErrors((prev) => ({ ...prev, confirmPassword: '' }));
                                }}
                                onBlur={() => {
                                  const confirm = passwordData.confirmPassword;
                                  const newPwd = passwordData.newPassword;
                                  if (!confirm?.trim()) setPasswordErrors((prev) => ({ ...prev, confirmPassword: 'Please confirm your new password' }));
                                  else if (newPwd && confirm !== newPwd) setPasswordErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }));
                                  else if (newPwd && confirm === newPwd) setPasswordErrors((prev) => ({ ...prev, confirmPassword: '' }));
                                }}
                                sx={{ mb: 2 }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                        edge="end"
                                        size="small"
                                      >
                                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <LoadingButton
                                  variant="contained"
                                  color="primary"
                                  onClick={handlePasswordChange}
                                  loading={isPasswordUpdating}
                                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                >
                                  Update Password
                                </LoadingButton>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  disabled={isPasswordUpdating}
                                  onClick={() => {
                                    setShowPasswordFields(false);
                                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                                    setPasswordErrors({ currentPassword: '', newPassword: '', confirmPassword: '' });
                                    setShowCurrentPassword(false);
                                    setShowNewPassword(false);
                                    setShowConfirmPassword(false);
                                  }}
                                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        )}
                      </Box>

                    <Divider sx={{ my: 4 }} />

                    {/* 2FA & Email Authentication Section */}
                    <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'success.main',
                      width: 40,
                      height: 40,
                      mr: 2,
                    }}
                  >
                    <SecurityIcon />
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                    Two-Factor Authentication & Security
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Email Verification */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    p: 2,
                    bgcolor: emailVerified ? 'success.lighter' : 'warning.lighter',
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EmailIcon
                      sx={{ color: emailVerified ? 'success.main' : 'warning.main' }}
                    />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#666' }}>
                        Email Verification
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {emailVerified
                          ? 'Your email is verified'
                          : 'Please verify your email address'}
                      </Typography>
                    </Box>
                  </Box>
                  {emailVerified ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Verified"
                      color="success"
                      sx={{ fontWeight: 600 }}
                    />
                  ) : (
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      onClick={handleEmailVerification}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                      Verify Email
                    </Button>
                  )}
                </Box>

                {/* 2FA Toggle */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    bgcolor: 'background.neutral',
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SecurityIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                        Two-Factor Authentication
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Add an extra layer of security to your account
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={twoFactorEnabled}
                        onChange={handleTwoFactorToggle}
                        color="success"
                        disabled={isEnabling2FA || isDisabling2FA}
                      />
                    }
                    label={
                      isEnabling2FA || isDisabling2FA
                        ? 'Processing...'
                        : twoFactorEnabled
                        ? 'Enabled'
                        : 'Disabled'
                    }
                  />
                </Box>

                {twoFactorEnabled && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Two-factor authentication is now enabled. You'll be required to enter a 4-digit
                    verification code sent to your phone ({profileData.phone}) when signing in.
                  </Alert>
                )}
                
                {!userDetails?.phone && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Please add a phone number to your profile before enabling two-factor authentication.
                  </Alert>
                )}
              </Box>

                  </TabPanel>

                  {/* Preferences Tab - only functional options (Theme) */}
                  <TabPanel value={currentTab} index={2}>
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Avatar sx={{ bgcolor: 'main.main', width: 40, height: 40, mr: 2 }}>
                          <PaletteIcon />
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'main.main' }}>
                          Display & Interface
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 3 }} />
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>Theme</InputLabel>
                            <Select
                              value={themeMode}
                              label="Theme"
                              onChange={(e) => changeThemeMode(e.target.value)}
                            >
                              <MenuItem value="light">Light</MenuItem>
                              <MenuItem value="dark">Dark</MenuItem>
                              <MenuItem value="auto">Auto (System)</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>
                  </TabPanel>
              </Box>
            </Card>
          </Grid>

          {/* Right Column - Quick Actions & Info */}
          <Grid item xs={12} lg={4}>
            {/* Quick Actions Card */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 3,
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                background: 'linear-gradient(135deg, #8E71AD 0%, #A94B9C 100%)',
              }}
            >
              <Box sx={{ p: { xs: 3, sm: 4 } }}>
                <Typography
                  variant="h6"
                  sx={{ color: 'white', fontWeight: 700, mb: 2 }}
                >
                  Quick Actions
                </Typography>
                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      bgcolor: 'white',
                      color: 'main.main',
                      '&:hover': { bgcolor: 'grey.100' },
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Download Account Data
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Privacy Settings
                  </Button>
                </Stack>
              </Box>
            </Card>

            {/* Account Security Status */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 3,
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
              }}
            >
              <Box sx={{ p: { xs: 3, sm: 4 } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'text.titles' }}>
                  Security Status
                </Typography>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      bgcolor: 'background.neutral',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2">Password Strength</Typography>
                    <Chip label="Strong" color="success" size="small" />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      bgcolor: 'background.neutral',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2">Email Verified</Typography>
                    <Chip
                      icon={emailVerified ? <CheckCircleIcon /> : null}
                      label={emailVerified ? 'Verified' : 'Pending'}
                      color={emailVerified ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      bgcolor: 'background.neutral',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2">2FA Enabled</Typography>
                    <Chip
                      label={twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      color={twoFactorEnabled ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Stack>
              </Box>
            </Card>

            {/* Help & Support */}
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
              }}
            >
              <Box sx={{ p: { xs: 3, sm: 4 } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'text.titles' }}>
                  Need Help?
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  If you have questions about your settings or need assistance, please contact
                  support.
                </Typography>
                <Button
                  variant="outlined"
                  color="main"
                  fullWidth
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Contact Support
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
      </Container>
    </>
  );
}

